import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Tag, GraduationCap, Flag } from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";
import { CATALOG } from "./Catalog";

const CALENDLY_URL = "https://calendly.com/ramzybaroody";

const PRINT_CSS = `
@media print {
  .no-print { display: none !important; }
  body { background: #fff !important; background-image: none !important; }
  @page { size: letter; margin: 0.4in; }
}
`;

export default function Flyer() {
  const { code } = useParams();
  const course = CATALOG.find((c) => c.code.toLowerCase() === (code || "").toLowerCase());
  if (!course) return <Navigate to="/catalog" replace />;

  const qrTarget = `${window.location.origin}/catalog`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=0b1d3a&data=${encodeURIComponent(qrTarget)}`;

  return (
    <div data-testid="flyer-page" className="min-h-screen bg-white">
      <style>{PRINT_CSS}</style>

      {/* Screen-only toolbar */}
      <div className="no-print sticky top-0 z-50 bg-[#0b1d3a] border-b border-[#d4af37]/30 px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/catalog" data-testid="flyer-back-link" className="inline-flex items-center gap-2 text-sm text-[#fff8e1]/80 hover:text-[#d4af37] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="flyer-print-btn"
          className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0b1d3a] px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#e2bd44] transition-colors"
        >
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      {/* Flyer sheet */}
      <div className="max-w-[8.5in] mx-auto bg-white text-[#0b1d3a]">
        {/* Header band */}
        <div className="bg-[#0b1d3a] px-8 py-5 flex items-center justify-between gap-4 border-b-4 border-[#d4af37]">
          <img src={LOGO_URL} alt="CFCS" className="h-12 w-auto rounded-sm" />
          <div className="text-right">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#d4af37] font-semibold">Course Flyer</p>
            <p className="text-xs text-[#fff8e1]/70 mt-0.5">Taught by a Ph.D. College Professor</p>
          </div>
        </div>

        {/* Title */}
        <div className="px-8 pt-7 pb-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#7d6b32]">{course.code} · One-time enrollment fee</p>
              <h1 data-testid="flyer-title" className="font-display text-3xl font-bold mt-1 leading-tight !text-[#0b1d3a]" style={{ color: "#0b1d3a" }}>{course.title}</h1>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#d4af37] bg-[#d4af37]/10 px-3 py-1 text-xs font-bold tracking-wide text-[#7d6b32] whitespace-nowrap">
              <Tag className="w-3 h-3" /> Contact for pricing
            </span>
          </div>
          {course.prerequisite && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#0b1d3a]/5 border border-[#0b1d3a]/15 px-3 py-1.5 text-xs font-semibold text-[#0b1d3a]/80">
              <GraduationCap className="w-3.5 h-3.5 text-[#7d6b32]" /> Prerequisite: {course.prerequisite}
            </p>
          )}
          <p className="mt-4 text-sm leading-relaxed text-[#0b1d3a]/85">{course.overview}</p>
        </div>

        {/* Topics */}
        <div className="px-8 pb-5">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#7d6b32] font-bold mb-2.5">What you'll learn</p>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {course.topics.map((t) => (
              <li key={t} className="flex items-start gap-2 text-xs text-[#0b1d3a]/80 leading-relaxed">
                <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#d4af37] shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Capstone */}
        <div className="mx-8 mb-6 rounded-lg border-2 border-[#d4af37] bg-[#fff8e1] p-4 flex items-start gap-3">
          <Flag className="w-5 h-5 text-[#7d6b32] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#7d6b32] font-bold mb-0.5">Capstone</p>
            <p className="text-xs text-[#0b1d3a]/85 leading-relaxed">{course.capstone}</p>
          </div>
        </div>

        {/* Footer: contact + QR */}
        <div className="bg-[#0b1d3a] text-[#fff8e1] px-8 py-6 flex items-center justify-between gap-6 border-t-4 border-[#d4af37]">
          <div className="space-y-1.5 text-sm">
            <p className="font-display text-lg font-bold text-[#d4af37]">Ready to enroll your team?</p>
            <p><span className="text-[#d4af37] font-semibold">Email</span>&nbsp;&nbsp;info@cfcorporatesolutions.com</p>
            <p><span className="text-[#d4af37] font-semibold">Book</span>&nbsp;&nbsp;{CALENDLY_URL.replace("https://", "")}</p>
            <p><span className="text-[#d4af37] font-semibold">Web</span>&nbsp;&nbsp;lms.cfcorporatesolutions.com/catalog</p>
          </div>
          <div className="text-center shrink-0">
            <div className="bg-white rounded-lg p-2 inline-block">
              <img src={qrSrc} alt="QR code — scan to view the full course catalog" data-testid="flyer-qr" className="w-[110px] h-[110px] block" />
            </div>
            <p className="text-[10px] text-[#fff8e1]/70 mt-1.5">Scan for the full catalog</p>
          </div>
        </div>

        <p className="text-center text-[10px] text-[#0b1d3a]/50 py-3">
          © {new Date().getFullYear()} {BRAND.name} — Built Around Your Business. Delivered for Results.
        </p>
      </div>
    </div>
  );
}
